import React, { useState, useRef, useEffect } from 'react';
import { CloudSun, ShoppingCart, User, MessageSquare, Menu, X, BookOpen, Leaf, ShieldCheck } from 'lucide-react';
import logo from '../assets/logo.png';

const Header = ({ onNavigate, active, user, onLogout }) => {
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef(null);

  // Check verified status for current user
  const isVerified = user && (JSON.parse(localStorage.getItem('verifiedUsers')) || []).includes(user.email);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) {
        setOpen(false);
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const NavButton = ({ id, icon: Icon, label }) => (
    <button
      onClick={() => onNavigate && onNavigate(id)}
      className={`flex items-center space-x-2 px-3 py-2 rounded-xl transition-all duration-200 ${active === id ? 'bg-emerald-100 text-emerald-800 shadow-sm' : 'text-slate-600 hover:text-emerald-700 hover:bg-emerald-50'}`}
    >
      <Icon className={`w-5 h-5 ${active === id ? 'text-emerald-600' : 'text-slate-500 group-hover:text-emerald-600'}`} />
      <span className="font-semibold">{label}</span>
    </button>
  );

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-white/20 shadow-sm transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <button onClick={() => onNavigate && onNavigate('home')} className="flex items-center space-x-3 cursor-pointer group">
            <div className="relative">
              <div className="absolute inset-0 bg-emerald-500 rounded-xl blur opacity-20 group-hover:opacity-40 transition"></div>
              <img src={logo} alt="AgriScan AI" className="relative w-11 h-11 rounded-xl shadow-md border border-white" />
            </div>
            <div className="text-left">
              <div className="text-2xl font-black text-slate-800 tracking-tight leading-none group-hover:text-emerald-700 transition">AgriScan AI</div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-600">Intelligent Farming</div>
            </div>
          </button>

          <button className="md:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition" onClick={() => setMenuOpen(v => !v)}>
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

          <div ref={rootRef} className="hidden md:flex items-center space-x-2 lg:space-x-4">
            <NavButton id="weather" icon={CloudSun} label="Weather" />
            <NavButton id="community" icon={MessageSquare} label="Community" />

            <NavButton id="schemes" icon={BookOpen} label="Schemes" />

            <div className="relative">
              <button onClick={() => { setOpen(v => !v); setProfileOpen(false); }} className={`flex items-center space-x-2 px-3 py-2 rounded-xl transition-all duration-200 ${active?.startsWith('market') ? 'bg-emerald-100 text-emerald-800' : 'text-slate-600 hover:text-emerald-700 hover:bg-emerald-50'}`}>
                <ShoppingCart className="w-5 h-5" />
                <span className="font-semibold">Market</span>
              </button>

              {open && (
                <div className="absolute right-0 mt-3 w-48 bg-white border border-slate-100 rounded-2xl shadow-xl overflow-hidden animate-fade-in-up">
                  <div className="p-1">
                    <button onClick={() => { onNavigate && onNavigate('market_buy'); setOpen(false); }} className="w-full text-left px-4 py-2.5 rounded-xl hover:bg-emerald-50 text-slate-700 font-medium transition flex items-center space-x-2">
                      <ShoppingCart className="w-4 h-4 text-emerald-500" />
                      <span>Buy Products</span>
                    </button>
                    <button onClick={() => { onNavigate && onNavigate('market_sell'); setOpen(false); }} className="w-full text-left px-4 py-2.5 rounded-xl hover:bg-emerald-50 text-slate-700 font-medium transition flex items-center space-x-2">
                      <Leaf className="w-4 h-4 text-emerald-500" />
                      <span>Sell Products</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="w-px h-8 bg-slate-200 mx-2"></div>

            <div className="relative">
              <button onClick={() => { setProfileOpen(v => !v); setOpen(false); }} className="flex items-center space-x-2 text-slate-700 hover:text-emerald-700 px-2 py-1.5 rounded-lg transition">
                <div className="w-9 h-9 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full flex items-center justify-center border-2 border-white shadow-sm relative">
                  {user ? (
                    <span className="font-bold text-emerald-700 text-lg">{user.name.charAt(0)}</span>
                  ) : (
                    <User className="w-5 h-5 text-emerald-700" />
                  )}
                  {isVerified && <div className="absolute -bottom-1 -right-1 bg-blue-500 text-white rounded-full p-0.5 border-2 border-white"><svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg></div>}
                </div>
              </button>
              {profileOpen && (
                <div className="absolute right-0 mt-3 w-64 bg-white border border-slate-100 rounded-2xl shadow-xl overflow-hidden animate-fade-in-up">
                  <div className="p-4 bg-slate-50 border-b border-slate-100">
                    <div className="flex items-center space-x-2 mb-1">
                      <p className="text-sm font-bold text-slate-800 truncate">{user ? user.name : 'Guest User'}</p>
                      {isVerified && <ShieldCheck className="w-4 h-4 text-blue-500 fill-blue-100" />}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{user ? user.email : 'Welcome to AgriScan'}</p>
                  </div>
                  <div className="p-1">
                    {user ? (
                      <button onClick={() => { if (onLogout) onLogout(); setProfileOpen(false); }} className="w-full text-left px-4 py-2.5 rounded-xl hover:bg-red-50 text-red-600 font-medium transition">Logout</button>
                    ) : (
                      <>
                        <button onClick={() => { onNavigate && onNavigate('login'); setProfileOpen(false); }} className="w-full text-left px-4 py-2.5 rounded-xl hover:bg-emerald-50 text-slate-700 font-medium transition">Login</button>
                        <button onClick={() => { onNavigate && onNavigate('signup'); setProfileOpen(false); }} className="w-full text-left px-4 py-2.5 rounded-xl hover:bg-emerald-50 text-slate-700 font-medium transition">Sign Up</button>
                        <div className="h-px bg-slate-100 my-1"></div>
                        <button onClick={() => { onNavigate && onNavigate('admin_login'); setProfileOpen(false); }} className="w-full text-left px-4 py-2.5 rounded-xl hover:bg-red-50 text-red-600 font-medium transition flex items-center justify-between">
                          <span>Admin Login</span>
                          <ShieldCheck className="w-4 h-4 ml-2" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu */}
          {menuOpen && (
            <div className="absolute left-4 right-4 top-[80px] md:hidden bg-white/95 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-4 space-y-2 animate-fade-in-up origin-top z-50">
              {user && (
                <div className="mb-4 p-3 bg-slate-50 rounded-xl flex items-center space-x-3 border border-slate-100">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center font-bold text-emerald-700 relative">
                    {user.name.charAt(0)}
                    {isVerified && <div className="absolute -bottom-0.5 -right-0.5 bg-blue-500 text-white rounded-full p-0.5 border-2 border-white"><svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg></div>}
                  </div>
                  <div className="overflow-hidden">
                    <div className="font-bold text-slate-800 flex items-center space-x-1">
                      <span className="truncate">{user.name}</span>
                      {isVerified && <ShieldCheck className="w-4 h-4 text-blue-500" />}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{user.email}</div>
                  </div>
                </div>
              )}

              <button onClick={() => { onNavigate && onNavigate('weather'); setMenuOpen(false); }} className="w-full p-3 rounded-xl hover:bg-emerald-50 flex items-center space-x-3 text-slate-700 font-medium">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600"><CloudSun className="w-5 h-5" /></div>
                <span>Weather</span>
              </button>
              <button onClick={() => { onNavigate && onNavigate('community'); setMenuOpen(false); }} className="w-full p-3 rounded-xl hover:bg-emerald-50 flex items-center space-x-3 text-slate-700 font-medium">
                <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600"><MessageSquare className="w-5 h-5" /></div>
                <span>Community</span>
              </button>
              <button onClick={() => { onNavigate && onNavigate('schemes'); setMenuOpen(false); }} className="w-full p-3 rounded-xl hover:bg-emerald-50 flex items-center space-x-3 text-slate-700 font-medium">
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600"><BookOpen className="w-5 h-5" /></div>
                <span>Govt Schemes</span>
              </button>
              <div className="h-px bg-slate-100 my-2"></div>
              <button onClick={() => { onNavigate && onNavigate('market_buy'); setMenuOpen(false); }} className="w-full p-3 rounded-xl hover:bg-emerald-50 flex items-center space-x-3 text-slate-700 font-medium">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600"><ShoppingCart className="w-5 h-5" /></div>
                <span>Buy Products</span>
              </button>
              <button onClick={() => { onNavigate && onNavigate('market_sell'); setMenuOpen(false); }} className="w-full p-3 rounded-xl hover:bg-emerald-50 flex items-center space-x-3 text-slate-700 font-medium">
                <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center text-green-600"><Leaf className="w-5 h-5" /></div>
                <span>Sell Products</span>
              </button>
              <div className="h-px bg-slate-100 my-2"></div>

              {!user ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <button onClick={() => { onNavigate && onNavigate('login'); setMenuOpen(false); }} className="w-full py-2.5 rounded-xl border border-slate-200 text-slate-700 font-semibold text-center">Login</button>
                    <button onClick={() => { onNavigate && onNavigate('signup'); setMenuOpen(false); }} className="w-full py-2.5 rounded-xl bg-emerald-600 text-white font-semibold text-center shadow-lg shadow-emerald-200">Sign Up</button>
                  </div>
                  <button onClick={() => { onNavigate && onNavigate('admin_login'); setMenuOpen(false); }} className="w-full py-2 rounded-xl text-red-500 font-medium text-center text-sm hover:bg-red-50 flex items-center justify-center space-x-1 mt-2">
                    <ShieldCheck className="w-4 h-4" />
                    <span>Admin Access</span>
                  </button>
                </>
              ) : (
                <button onClick={() => { if (onLogout) onLogout(); setMenuOpen(false); }} className="w-full py-2.5 rounded-xl bg-red-50 text-red-600 font-semibold text-center border border-red-100">Sign Out</button>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
